import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import * as actions from '../../actions'
import {useSchema, DetailsPage} from '../schema-forms'
import { desireSchema } from './desireSchema'
import { sortBy } from '../common/dropdown'
import { error } from '../common/toast'

const renameMinMax = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    else obj = {...obj}  // shallow copy
    if ('min' in obj) obj.minimum = obj.min
    if ('max' in obj) obj.maximum = obj.max
    delete obj.min
    delete obj.max
    return obj
}

const DIALOG_SHAPES = new Set(['RB','OV','EM','RAD','CU','PR','PS','MQ'])
const DIALOG_CLARITIES = new Set(['VS1','VS2','SI1','SI2'])
const DIALOG_MIN_WEIGHT = 0.30
const DIALOG_MAX_WEIGHT = 2.99
const DIALOG_CUTPOLSYM_OPTIONS = new Set(['EX','VG'])

const DesireForm = ({shapeList, colourList, clarityList, fluorList, gradeList, savedFilters, ...props}) => {

    // Initialize criteria from currentFilters
    const [criteria] = useState(() => {
        const filters = savedFilters['available'] || []
         return {
            weight: renameMinMax(filters.find(f => f.id === 'weight')?.value),
            shape: filters.find(f => f.id === 'shapeCode')?.value,
            colour: filters.find(f => f.id === 'colour')?.value,
            clarity: filters.find(f => f.id === 'clarity')?.value,
            price: renameMinMax(filters.find(f => f.id === 'total')?.value),
            fluorescence: filters.find(f => f.id === 'fluorescence')?.value,
            cut: filters.find(f => f.id === 'cut')?.value,
            polish: filters.find(f => f.id === 'polish')?.value,
            symmetry: filters.find(f => f.id === 'symmetry')?.value,
            lwRatio: renameMinMax(filters.find(f => f.id === 'lwRatio')?.value),
            tablePercent: renameMinMax(filters.find(f => f.id === 'tablePct')?.value),
            depthPercent: renameMinMax(filters.find(f => f.id === 'depthPct')?.value),
            quantity: 1,
            customerId: props.customerId
        }
    })
    const schema = useSchema(desireSchema, criteria)

    const onClose = () => {
        props.selectTab('Available.Loose Diamonds')
    }
    const handleSubmit = async () => {
        const unValidated = schema.isUnValidated()
        const validation = schema.validate()
        const errors = (validation || []).filter(x => x.type !== 'warning')
        if (validation?.length && (unValidated || errors.length)) {
            for (const err of errors) {
                error(err.message)
            }
            throw new Error(validation.map(v => v.message).join(', '))
        }

        schema.onChange({status: hackyDisonsCriteria(schema.get())})
        const unValidated2 = schema.isUnValidated()
        const validation2 = schema.validate()
        const errors2 = (validation2 || []).filter(x => x.type !== 'warning')
        if (validation?.length && (unValidated2 || errors2.length)) {
            for (const err of errors2) {
                error(err.message)
            }
            throw new Error(validation2.map(v => v.message).join(', '))
        }

        const preSubmit = schema.getPost()
        const toSubmit = schema.transform(preSubmit)
        console.log('toSubmit', toSubmit)
       props.submitDesiredStones(toSubmit)
        .then(() => {
            onClose()
        })
    }

    const possibleStones = useMemo(() => {
        return (props.inventory || []).filter(stone => {
            if (schema.data.shape && !schema.get('shape').includes(stone.shapeCode)) return false
            if (schema.data.colour && !schema.get('colour').includes(stone.colour)) return false
            if (schema.data.clarity && !schema.get('clarity').includes(stone.clarity)) return false
            if (schema.data.cut && !schema.get('cut').includes(stone.cut)) return false
            if (schema.data.polish && !schema.get('polish').includes(stone.polish)) return false
            if (schema.data.symmetry && !schema.get('symmetry').includes(stone.symmetry)) return false
            if (schema.data.fluorescence && !schema.get('fluorescence').includes(stone.fluorescence)) return false
        })
    }, [props.inventory, schema.get()])

    function hackyDisonsCriteria({shortTerm, shape, weight, clarity}) {
        if (shortTerm !== 'true') return 'ACTIVE'
        if (shape != null && shape.length) {
            const reqShapes = new Set(shape)
            if (reqShapes.isDisjointFrom(DIALOG_SHAPES)) return 'DISONS'
        }
        if (clarity != null && clarity.length) {
            const reqClarities = new Set(clarity)
            if (reqClarities.isDisjointFrom(DIALOG_CLARITIES)) return 'DISONS'
        }
        if (weight != null && (weight.minimum != null || weight.maximum != null)) {
            const min = weight.minimum != null ? weight.minimum : 0
            const max = weight.maximum != null ? weight.maximum : 100
            if (min > DIALOG_MAX_WEIGHT || max < DIALOG_MIN_WEIGHT) return 'DISONS'
        }
        // console.log('shortTerm', shortTerm, 'shape', shape, DIALOG_SHAPES.isDisjointFrom(new Set(shape)), 'weight', weight, (weight?.minimum != null ? weight.minimum : 0) > DIALOG_MAX_WEIGHT || (weight?.maximum != null ? weight.maximum : 100) < DIALOG_MIN_WEIGHT, 'clarity', clarity, DIALOG_CLARITIES.isDisjointFrom(new Set(clarity)))
        return 'ACTIVE'
    }

    const allowedGrades = gradeList?.filter(g => (g.gradeSet==='Standard' && DIALOG_CUTPOLSYM_OPTIONS.has(g.grade)) || (schema.get('cut')?.includes?.(g.grade) || schema.get('polish')?.includes?.(g.grade) || schema.get('symmetry')?.includes?.(g.grade)))

    return (
        <div className='confirmation-container outer border'>
            <div className='confirmation-column' style={{width: '100%'}}>
                <div className='confirmation-heading container-h-margin'>Detailed Search</div>
                <div className='confirmation-text container-h-margin'>
                    If you can't find the diamond you are looking for, quickly fill out the details below and we will do all the work to get you exactly what you're looking for &ndash; hang tight!
                    <hr/>
                </div>

                <DetailsPage
                    schema={schema}
                    //title='Diamond Specifications'
                    onSubmit={handleSubmit}
                    editOnly={true}
                    className='desire-form'
                    buttonProps={{cancel: {onClick: onClose}, submit: schema.get('shortTerm')==null ? {disabled: true} : {}}}
                    fields={[
                        {
                            component: 'wizard', 
                            span: true, 
                            bottomNavButtons: true, 
                            className: 'no-tabs',
                            pages: [
                                {
                                    title: 'Search Criteria',
                                    //className: 'narrow',
                                    fields: [
                                        {
                                            name: 'shape', 
                                            label: 'Shape', 
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('description'), optionKey: 'shapeCode'}, 
                                            options: shapeList?.filter(s => !s.hideAvailable)
                                        },
                                        {
                                            name: 'colour', 
                                            label: 'Colour', 
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('order')},
                                            options: colourList
                                        },
                                        {
                                            name: 'weight', 
                                            component: 'fieldSet', 
                                            label: 'Weight',
                                            fields: [
                                                {name: 'weight.minimum', label: 'Min', component: 'numberInput'},
                                                {name: 'weight.maximum', label: 'Max', component: 'numberInput'}
                                            ]
                                        },
                                        {
                                            name: 'clarity', 
                                            label: 'Clarity',
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('order')},
                                            options: clarityList
                                        },
                                        {
                                            name: 'cut', 
                                            label: 'Cut',
                                            component: 'dropdown',
                                            componentProps: {isMulti: true, sortFunc: sortBy('order'), optionKey: 'grade'},
                                            options: allowedGrades
                                        },
                                        {
                                            name: 'fluorescence', 
                                            label: 'Fluorescence', 
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('order')}, 
                                            options: fluorList
                                        },
                                        {
                                            name: 'polish', 
                                            label: 'Polish', 
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('order'), optionKey: 'grade'}, 
                                            options: allowedGrades
                                        },
                                        {
                                            name: 'lwRatio',
                                            label: 'L/W\u00A0Ratio',
                                            component: 'fieldSet',
                                            disabled: Array.isArray(schema.get('shape')) && schema.get('shape').includes('RB') && schema.get('shape').length===1,
                                            fields: [
                                                {name: 'lwRatio.minimum', label: 'Min', component: 'numberInput'},
                                                {name: 'lwRatio.maximum', label: 'Max', component: 'numberInput'}
                                            ]
                                        },
                                        {
                                            name: 'symmetry', 
                                            label: 'Symmetry', 
                                            component: 'dropdown', 
                                            componentProps: {isMulti: true, sortFunc: sortBy('order'), optionKey: 'grade'}, 
                                            options: allowedGrades
                                        },
                                        {
                                            name: 'priceWrapper',
                                            label: 'Max\u00A0Price',
                                            component: 'fieldSet',
                                            fields: [{
                                                name: 'price.maximum',
                                                //label: 'Max\u00A0Price:',
                                                component: 'numberInput',
                                                span: true,
                                                /*fields: [
                                                    {name: 'price.minimum', label: 'Min', component: 'numberInput'},
                                                    {name: 'price.maximum', label: 'Max', component: 'numberInput'}
                                                ]*/
                                            }]
                                        },
                                        /*{
                                            name: 'depthPercent',
                                            label: 'Depth\u00A0%:',
                                            component: 'fieldSet',
                                            fields: [
                                                {name: 'depthPercent.minimum', label: 'Min', component: 'numberInput'},
                                                {name: 'depthPercent.maximum', label: 'Max', component: 'numberInput'}
                                            ]
                                        },*/
                                        /*{
                                            name: 'tablePercent',
                                            label: 'Table\u00A0%:',
                                            component: 'fieldSet',
                                            fields: [
                                                {name: 'tablePercent.minimum', label: 'Min', component: 'numberInput'},
                                                {name: 'tablePercent.maximum', label: 'Max', component: 'numberInput'}
                                            ]
                                        }*/
                                    ]
                                },
                                {
                                    title: 'Available Options',
                                    hidden: !possibleStones.length,
                                    fields: [
                                        //{name: 'dlgOptions', label: 'Available On Dialog', component: 'table', span: true, }
                                    ]
                                },
                                {
                                    title: 'Confirmation',
                                    fields: [
                                        {
                                            name: 'shortTerm', 
                                            label: 'Request Duration', 
                                            component: 'radioGroup', 
                                            options: [
                                                {value: 'true', label: 'Short Term (10 days)'},
                                                {value: 'false', label: 'Long Term'}
                                            ],
                                            afterChange: (change) => {
                                                // console.log('changed radio', change, schema.get())
                                                schema.onChange({status: hackyDisonsCriteria({...schema.get(), ...change})})
                                            }
                                        },
                                        {name: 'remarks', label: 'Additional Comments or Requirements', component: 'textArea', componentProps: {style: {flexGrow: 1}}, span: true, rows: 4}
                                    ]
                                }
                            ]
                        }
                    ]}
                />
            </div>
        </div>
    )
}

function mapStateToProps(state) {
    return {
        shapeList: state.attributes.shapes || [],
        colourList: state.attributes.colours || [],
        clarityList: state.attributes.clarities || [],
        fluorList: state.attributes.fluorescences || [],
        gradeList: state.attributes.grades || [],
        admin: state.user && state.user.currentUser ? state.user.currentUser.administrator : false,
        savedFilters: state.tabs.savedFilters || {},
        customerId: state.user.customer?.customerId
    }
}

export default connect(mapStateToProps, actions)(DesireForm);